import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth.js';
import RequireAuthRoute from './routes/RequireAuthRoute.js';
import PublicOnlyRoute from './routes/PublicOnlyRoute.js';
import { AUTHENTICATED_HOME_PATH, PUBLIC_HOME_PATH } from '../shared/routes.js';
import { seoLandingPages } from '../shared/seo-landing-pages.js';
import AppCrashFallback from './components/common/AppCrashFallback.js';
import {
  AuthPageSkeleton,
  ProtectedAppShellSkeleton,
  ProjectsHomePageSkeleton,
} from './components/common/LoadingSkeletons.js';
import {
  loadDashboardLayout,
  loadGlobalSettingsLayout,
  loadInsightsLayout,
  loadProjectsHomeLayout,
  loadSettingsLayout,
  protectedLayoutChunkLoaders,
} from './lib/routes/protectedPreload.js';

const SignIn = React.lazy(() => import('./components/Auth/SignIn.js'));
const SignUp = React.lazy(() => import('./components/Auth/SignUp.js'));
const ConfirmEmail = React.lazy(() => import('./components/Auth/ConfirmEmail.js'));
const ForgotPassword = React.lazy(() => import('./components/Auth/ForgotPassword.js'));
const ResetPassword = React.lazy(() => import('./components/Auth/ResetPassword.js'));
const VideoPage = React.lazy(() => import('./components/Auth/VideoPage.js'));
const TermsPage = React.lazy(() => import('./components/Public/TermsPage.js'));
const PrivacyPage = React.lazy(() => import('./components/Public/PrivacyPage.js'));
const ChangelogPage = React.lazy(() =>
  import('./components/Public/ChangelogPage.js').then((mod) => ({ default: mod.ChangelogPage })),
);
const SeoLandingPage = React.lazy(() => import('./components/Public/SeoLandingPage.js'));
const DashboardLayout = React.lazy(loadDashboardLayout);
const SettingsLayout = React.lazy(loadSettingsLayout);
const GlobalSettingsLayout = React.lazy(loadGlobalSettingsLayout);
const ProjectsHomeLayout = React.lazy(loadProjectsHomeLayout);
const InsightsLayout = React.lazy(loadInsightsLayout);

const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-TV5D2SX4EM';

/**
 * Warm the lazy layout chunks once the user is authenticated so crossing a
 * layout boundary (ProjectsHome ↔ Dashboard ↔ Settings) doesn't pay a chunk
 * download behind a full-page skeleton.
 */
function ProtectedLayoutChunkPreloader() {
  const { user, isLoading } = useAuth();
  const hasPreloadedRef = React.useRef(false);

  React.useEffect(() => {
    if (hasPreloadedRef.current || isLoading || !user) {
      return;
    }

    const preload = () => {
      hasPreloadedRef.current = true;
      for (const loadChunk of protectedLayoutChunkLoaders) {
        void loadChunk().catch(() => undefined);
      }
    };

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(preload, { timeout: 3_000 });
      return () => window.cancelIdleCallback(handle);
    }

    const timer = window.setTimeout(preload, 1_000);
    return () => window.clearTimeout(timer);
  }, [isLoading, user]);

  return null;
}

function LegacyAuthRedirect({ to }: { to: '/login' | '/register' }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

function LegacyVideoRedirect() {
  const location = useLocation();
  return <Navigate to={`${PUBLIC_HOME_PATH}${location.search}${location.hash}`} replace />;
}

function PublicRouteFallback({ children }: { children: React.ReactNode }) {
  return (
    <RouteErrorBoundary>
      <React.Suspense fallback={<AuthPageSkeleton />}>{children}</React.Suspense>
    </RouteErrorBoundary>
  );
}

function ProtectedRouteFallback({
  children,
  variant = 'default',
}: {
  children: React.ReactNode;
  variant?: 'default' | 'projects';
}) {
  const fallback =
    variant === 'projects' ? <ProjectsHomePageSkeleton /> : <ProtectedAppShellSkeleton />;

  return (
    <RouteErrorBoundary>
      <React.Suspense fallback={fallback}>{children}</React.Suspense>
    </RouteErrorBoundary>
  );
}

class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: unknown; componentStack: string }
> {
  override state: { error: unknown; componentStack: string } = {
    error: null,
    componentStack: '',
  };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override componentDidCatch(_error: unknown, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? '' });
  }

  override render() {
    if (this.state.error) {
      return (
        <AppCrashFallback
          error={this.state.error}
          exceptionEvent={null}
          componentStack={this.state.componentStack}
        />
      );
    }

    return this.props.children;
  }
}

function MarketingHomeRoute() {
  const { user, isLoading } = useAuth();

  if (!isLoading && user) {
    return <Navigate to={AUTHENTICATED_HOME_PATH} replace />;
  }

  return (
    <PublicRouteFallback>
      <VideoPage />
    </PublicRouteFallback>
  );
}

function GoogleAnalyticsPageViews() {
  const location = useLocation();
  const hasTrackedInitialView = React.useRef(false);

  React.useEffect(() => {
    if (!window.gtag) {
      return;
    }

    if (!hasTrackedInitialView.current) {
      hasTrackedInitialView.current = true;
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      window.gtag?.('event', 'page_view', {
        page_location: window.location.href,
        page_path: `${location.pathname}${location.search}${location.hash}`,
        page_title: document.title,
        send_to: GOOGLE_ANALYTICS_MEASUREMENT_ID,
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [location.hash, location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <GoogleAnalyticsPageViews />
        <ProtectedLayoutChunkPreloader />
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <PublicRouteFallback>
                  <SignIn />
                </PublicRouteFallback>
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnlyRoute>
                <PublicRouteFallback>
                  <SignUp />
                </PublicRouteFallback>
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/register/confirm-email"
            element={
              <PublicOnlyRoute>
                <PublicRouteFallback>
                  <ConfirmEmail />
                </PublicRouteFallback>
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRouteFallback>
                <ForgotPassword />
              </PublicRouteFallback>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRouteFallback>
                <ResetPassword />
              </PublicRouteFallback>
            }
          />
          <Route path="/video" element={<LegacyVideoRedirect />} />
          <Route path="/sign-in" element={<LegacyAuthRedirect to="/login" />} />
          <Route path="/sign-up" element={<LegacyAuthRedirect to="/register" />} />
          <Route
            path="/tos"
            element={
              <PublicRouteFallback>
                <TermsPage />
              </PublicRouteFallback>
            }
          />
          <Route
            path="/privacy"
            element={
              <PublicRouteFallback>
                <PrivacyPage />
              </PublicRouteFallback>
            }
          />
          <Route
            path="/changelog"
            element={
              <PublicRouteFallback>
                <ChangelogPage />
              </PublicRouteFallback>
            }
          />
          {seoLandingPages.map((page) => (
            <Route
              key={page.path}
              path={page.path}
              element={
                <PublicRouteFallback>
                  <SeoLandingPage path={page.path} />
                </PublicRouteFallback>
              }
            />
          ))}
          <Route path={PUBLIC_HOME_PATH} element={<MarketingHomeRoute />} />
          <Route
            path={AUTHENTICATED_HOME_PATH}
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback variant="projects">
                  <ProjectsHomeLayout />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route
            path="/projects/:projectId/settings"
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback>
                  <SettingsLayout />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route
            path="/settings/account"
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback>
                  <GlobalSettingsLayout section="account" />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route
            path="/settings/billing"
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback>
                  <GlobalSettingsLayout section="billing" />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route
            path="/settings/upgrade"
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback>
                  <GlobalSettingsLayout section="upgrade" />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback>
                  <InsightsLayout />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route
            path="/projects/:projectId/collections"
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback>
                  <DashboardLayout />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route
            path="/projects/:projectId/:collectionName/:section"
            element={
              <RequireAuthRoute>
                <ProtectedRouteFallback>
                  <DashboardLayout />
                </ProtectedRouteFallback>
              </RequireAuthRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
